import type { EfteruddannelseIndberetningStatusId } from "@/api/person/paabud";
import {
  EfteruddannelseIndberetningStatusId as EfteruddannelseIndberetningStatusEnum,
  type PersonEfteruddannelse,
} from "@/interfaces/schema";

import { getIndberetningFrist } from "../Tilsyn/util";

import {
  getCarriedInLessons,
  getTransferredOutLessons,
} from "./transferredLessons";

export type DecoratedPersonEfteruddannelse = PersonEfteruddannelse & {
  indberetningStatus?: string;
  indberetningStatusId?: EfteruddannelseIndberetningStatusId;
  registreringsDato?: string;
  signaturNavn?: string;
  indberetningId?: string;
  /** Lektioner overført IND i perioden (Særlig/Påbud/Pålæg på perioden selv). */
  lektionerOverfoert?: number;
  /** Lektioner overført UD til næste periode (næste periodes Særlig). */
  lektionerOverfoertTilNaeste?: number;
  kanSletteIndberetninger?: boolean;
  indberetningFrist?: string;
};

/**
 * The GraphQL API returns the status as the raw enum value (e.g.
 * "FRIST_ORDINAER"), whereas the label/variant mappers key on the camelCase
 * enum member names (e.g. "FristOrdinaer"). Reverse-lookup the member name so
 * the stored value matches its declared type and renders as a proper label.
 */
export const normalizeIndberetningStatusId = (
  raw: EfteruddannelseIndberetningStatusEnum | string | null | undefined,
): EfteruddannelseIndberetningStatusId | undefined => {
  if (!raw) return undefined;
  const memberName = (
    Object.keys(
      EfteruddannelseIndberetningStatusEnum,
    ) as EfteruddannelseIndberetningStatusId[]
  ).find((name) => EfteruddannelseIndberetningStatusEnum[name] === raw);
  // Fall back to the raw value if it is already a member name.
  return memberName ?? (raw as EfteruddannelseIndberetningStatusId);
};

export const decoratePersonEfteruddannelse = (
  pe: PersonEfteruddannelse,
  allPeriods: PersonEfteruddannelse[] = [],
): DecoratedPersonEfteruddannelse => {
  const decoration: DecoratedPersonEfteruddannelse = { ...pe };
  decoration.lektionerOverfoert = getCarriedInLessons(pe);
  decoration.lektionerOverfoertTilNaeste = getTransferredOutLessons(
    pe,
    allPeriods,
  );
  const periodeudloebIndberetning = pe.personEfteruddannelseIndberetning.find(
    (indberetning) =>
      indberetning.efteruddannelseIndberetningstype === "PERIODEUDLOEB",
  );
  if (periodeudloebIndberetning) {
    decoration.indberetningStatus =
      periodeudloebIndberetning.efteruddannelseIndberetningStatusNavigation
        ?.navn ?? "";
    decoration.indberetningStatusId = normalizeIndberetningStatusId(
      periodeudloebIndberetning.efteruddannelseIndberetningStatus,
    );
    decoration.registreringsDato =
      periodeudloebIndberetning.registreringsDato ?? "";
    decoration.signaturNavn = periodeudloebIndberetning.signaturNavn ?? "";
    decoration.indberetningId = periodeudloebIndberetning.id;
  }
  decoration.indberetningFrist = getIndberetningFrist(pe) ?? undefined;

  return decoration;
};

export const decoratePersonEfteruddannelseList = (
  periods: PersonEfteruddannelse[] | null | undefined,
): DecoratedPersonEfteruddannelse[] =>
  periods?.map((pe) => decoratePersonEfteruddannelse(pe, periods)) ?? [];
